import { DropdownMenu, useClickOutside, useDropdownDirection } from '@consigli/facade';
import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getMeetingId, type SupportedItem } from './action-item-types';
import { TopicDropdownContent } from './topic-dropdown-content';
import { TopicDropdownFooter } from './topic-dropdown-footer';
import { useActionItemMutations } from '../hooks/use-action-item-mutations';

interface EditableTopicProps {
  item: SupportedItem;
  meetingId?: string;
  topics: string[];
  onUpdate?: (updated: SupportedItem) => void;
  onAddCustomTopic?: (topic: string) => void;
}

export const EditableTopic: FC<EditableTopicProps> = ({
  item,
  meetingId: propMeetingId,
  topics,
  onUpdate,
  onAddCustomTopic,
}) => {
  const { t } = useTranslation();
  const resolvedMeetingId = getMeetingId(item, propMeetingId);
  const { updateItem } = useActionItemMutations(resolvedMeetingId);

  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const openDirection = useDropdownDirection({
    containerRef,
    isOpen: showDropdown,
    dropdownHeight: 240,
  });

  const handleClickOutside = useCallback(() => {
    setShowDropdown(false);
  }, []);

  useClickOutside({
    ref: containerRef,
    onClickOutside: handleClickOutside,
    enabled: showDropdown,
  });

  const updateTopic = async (newTopic: string | null, isCustom = false) => {
    if (newTopic === (item.topic ?? null)) {
      setShowDropdown(false);
      return;
    }

    try {
      await updateItem(item.id, {
        topic: newTopic,
      });
      onUpdate?.({ ...item, topic: newTopic } as SupportedItem);
      if (isCustom && newTopic) {
        onAddCustomTopic?.(newTopic);
      }
      toast.success(t('meeting-secretary.action-items.item-updated'));
    } catch {
      toast.error(t('meeting-secretary.meetings.error-updating-item'));
    }
    setShowDropdown(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowDropdown((prev) => !prev);
        }}
        className="text-left text-gray-600 hover:text-gray-900 hover:underline transition-colors cursor-pointer w-full truncate"
        title={item.topic || t('meeting-secretary.action-items.no-topic', 'No Topic')}
      >
        {item.topic || '—'}
      </button>

      <DropdownMenu
        isOpen={showDropdown}
        direction={openDirection}
        header={t('meeting-secretary.action-items.select-topic', 'Select Topic')}
        width="w-56"
        scrollable
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
        footer={
          <TopicDropdownFooter
            isOpen={showDropdown}
            onAddCustomTopic={(topic) => updateTopic(topic, true)}
          />
        }
      >
        <TopicDropdownContent
          topics={topics}
          selectedTopic={item.topic}
          showClearOption={!!item.topic}
          onSelect={(topic) => updateTopic(topic)}
          onClear={() => updateTopic(null)}
        />
      </DropdownMenu>
    </div>
  );
};
